import React, { useState, useEffect, useCallback } from 'react';
import { Database, RefreshCw, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const COUNTRIES = [
    { code: 'de', label: 'Deutschland', flag: '🇩🇪' },
    { code: 'at', label: 'Österreich', flag: '🇦🇹' },
    { code: 'ch', label: 'Schweiz', flag: '🇨🇭' },
];

const formatNumber = (n) => {
    if (n === null || n === undefined) return '-';
    return new Intl.NumberFormat('de-DE').format(n);
};

const formatDate = (s) => {
    if (!s) return 'noch nie';
    try {
        const d = new Date(s.replace(' ', 'T'));
        if (Number.isNaN(d.getTime())) return s;
        return new Intl.DateTimeFormat('de-DE', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        }).format(d);
    } catch {
        return s;
    }
};

const cardStyle = {
    background: 'var(--bg-card, #ffffff)',
    border: '1px solid var(--border-light, #e2e8f0)',
    borderRadius: '12px',
    padding: '1.25rem',
    boxShadow: '0 1px 3px rgba(44, 62, 80, 0.04)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.875rem',
};

const labelStyle = {
    fontSize: '0.75rem',
    color: 'var(--text-secondary, #64748b)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontWeight: 600,
};

const valueStyle = {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: 'var(--text-primary, #2c3e50)',
};

const buttonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.875rem',
    borderRadius: '8px',
    border: 'none',
    background: 'var(--accent-slate, #5b7c8d)',
    color: '#ffffff',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'opacity 0.2s ease',
};

const ZipDatabaseIndex = () => {
    const [status, setStatus] = useState({});
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState({});
    const [results, setResults] = useState({});
    const [error, setError] = useState(null);

    const loadStatus = useCallback(async () => {
        try {
            const res = await fetch('index.php/admin/getZipDatabaseStatus');
            const data = await res.json();
            if (data.success) {
                setStatus(data.data || {});
                setError(null);
            } else {
                setError(data.error || 'Status konnte nicht geladen werden');
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    const runUpdate = useCallback(async (code) => {
        setRunning(prev => ({ ...prev, [code]: true }));
        setResults(prev => ({ ...prev, [code]: null }));
        try {
            const body = new URLSearchParams();
            body.append('country', code);
            const res = await fetch('index.php/admin/updateZipDatabase', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString(),
            });
            const data = await res.json();
            if (data.success) {
                setResults(prev => ({ ...prev, [code]: { ok: true, data } }));
                await loadStatus();
            } else {
                setResults(prev => ({ ...prev, [code]: { ok: false, error: data.error || 'Update fehlgeschlagen' } }));
            }
        } catch (e) {
            setResults(prev => ({ ...prev, [code]: { ok: false, error: e.message } }));
        } finally {
            setRunning(prev => ({ ...prev, [code]: false }));
        }
    }, [loadStatus]);

    const runAll = useCallback(async () => {
        for (const c of COUNTRIES) {
            await runUpdate(c.code);
        }
    }, [runUpdate]);

    const anyRunning = Object.values(running).some(Boolean);

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                    <Database size={24} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary, #2c3e50)' }}>
                            PLZ-Datenbank
                        </h2>
                        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary, #64748b)' }}>
                            Aktualisiert die PLZ/Ort-Tabellen aus offenen GeoNames-Daten.
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={runAll}
                    disabled={anyRunning || loading}
                    style={{ ...buttonStyle, opacity: (anyRunning || loading) ? 0.6 : 1 }}
                >
                    {anyRunning ? <Loader2 size={16} className="spinner-border spinner-border-sm" /> : <RefreshCw size={16} />}
                    Alle Länder aktualisieren
                </button>
            </div>

            {error && (
                <div style={{
                    background: 'rgba(239, 68, 68, 0.08)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#b91c1c',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                }}>
                    <AlertCircle size={16} /> {error}
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
                {COUNTRIES.map(country => {
                    const s = status[country.code] || {};
                    const r = results[country.code];
                    const isRunning = !!running[country.code];
                    return (
                        <div key={country.code} style={cardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontSize: '1.5rem' }}>{country.flag}</span>
                                <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary, #2c3e50)' }}>{country.label}</span>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                                <div>
                                    <div style={labelStyle}>Einträge</div>
                                    <div style={valueStyle}>{loading ? '…' : formatNumber(s.rows)}</div>
                                </div>
                                <div>
                                    <div style={labelStyle}>Letztes Update</div>
                                    <div style={{ ...valueStyle, fontSize: '0.95rem' }}>
                                        {loading ? '…' : formatDate(s.last_updated)}
                                    </div>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => runUpdate(country.code)}
                                disabled={isRunning || anyRunning}
                                style={{ ...buttonStyle, opacity: (isRunning || anyRunning) ? 0.6 : 1 }}
                            >
                                {isRunning ? <Loader2 size={16} className="spinner-border spinner-border-sm" /> : <RefreshCw size={16} />}
                                {isRunning ? 'Aktualisiere…' : 'Jetzt aktualisieren'}
                            </button>

                            {r && r.ok && (
                                <div style={{
                                    background: 'rgba(16, 185, 129, 0.08)',
                                    border: '1px solid rgba(16, 185, 129, 0.3)',
                                    color: '#047857',
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '6px',
                                    fontSize: '0.8125rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                }}>
                                    <CheckCircle size={14} />
                                    <span>
                                        {formatNumber(r.data.total_rows)} Einträge
                                        {typeof r.data.previous_rows === 'number' && (
                                            <> · vorher {formatNumber(r.data.previous_rows)}</>
                                        )}
                                        {typeof r.data.duration_ms === 'number' && (
                                            <> · {(r.data.duration_ms / 1000).toFixed(1)}s</>
                                        )}
                                    </span>
                                </div>
                            )}
                            {r && !r.ok && (
                                <div style={{
                                    background: 'rgba(239, 68, 68, 0.08)',
                                    border: '1px solid rgba(239, 68, 68, 0.3)',
                                    color: '#b91c1c',
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '6px',
                                    fontSize: '0.8125rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                }}>
                                    <AlertCircle size={14} /> {r.error}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <p style={{ marginTop: '1.5rem', fontSize: '0.8125rem', color: 'var(--text-secondary, #64748b)' }}>
                Datenquelle: <a href="https://download.geonames.org/export/zip/" target="_blank" rel="noopener noreferrer">GeoNames Postal Codes</a> · Lizenz: CC BY 4.0
            </p>
        </div>
    );
};

export default ZipDatabaseIndex;
